/**
 * Serialise a column filter's selected values into the `cf.<Column>.in=`
 * query-param format the backend's `_split_in_values` parses (see
 * lara-data-plane `src/reporting/services/reporting_service.py`).
 *
 * A plain comma-join loses any value that itself contains a comma
 * ("Foo, Ltd") — the backend splitter is escape-aware instead: `\,` for a
 * literal comma, `\\` for a literal backslash. A selection of ONLY the
 * blank value is sent as the two-character string `","` (not empty `""`,
 * which the backend's placeholder-vs-column-filter split would silently
 * drop as an empty query param) — it splits back to `["", ""]`, which
 * collapses to the NULL/empty-string predicate.
 */
export function serialiseInParam(values: string[]): string {
  if (values.length === 1 && values[0] === "") {
    return ",";
  }
  return values
    .map((v) => v.replace(/\\/g, "\\\\").replace(/,/g, "\\,"))
    .join(",");
}

/** Inverse of `serialiseInParam`: splits on unescaped commas, unescapes `\,` and `\\`. */
export function parseInParam(stored: string): string[] {
  if (!stored) return [];
  const parts: string[] = [];
  let current = "";
  for (let i = 0; i < stored.length; i++) {
    const ch = stored[i];
    if (ch === "\\" && i + 1 < stored.length) {
      current += stored[i + 1];
      i++;
    } else if (ch === ",") {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return [...new Set(parts)];
}

/** One `cf.{column}.{op}` search entry, read back for display. */
export type ColumnFilterSearchEntry = {
  column: string;
  operator: string;
  value: string;
};

/**
 * The `cf.{column}.{op}` entries in a page's search params, in key order, for
 * a page that names the slice it was opened with (the datasource admin page,
 * chart-interactions-seam D7). The operator is the text after the LAST dot, so
 * a column name may itself contain dots. An `in` value is split with
 * `parseInParam` and joined for display; a blank shows as "(blank)".
 */
export function parseColumnFilterSearch(
  search: Record<string, unknown>,
): ColumnFilterSearchEntry[] {
  const show = (v: string) => (v === "" ? "(blank)" : v);
  const out: ColumnFilterSearchEntry[] = [];
  for (const [key, raw] of Object.entries(search)) {
    if (!key.startsWith("cf.") || typeof raw !== "string") continue;
    const rest = key.slice(3);
    const dot = rest.lastIndexOf(".");
    if (dot <= 0 || dot === rest.length - 1) continue;
    const column = rest.slice(0, dot);
    const operator = rest.slice(dot + 1);
    const value =
      operator === "in" ? parseInParam(raw).map(show).join(", ") : show(raw);
    out.push({ column, operator, value });
  }
  return out;
}

/**
 * A value for a hand-composed portal link (`new URLSearchParams`), quoted the
 * way the router's own `stringifySearch` quotes it: the router runs
 * `JSON.parse` on every search value, so `1.50`, `1e3` or `null` would come
 * back as a number or null. Text that parses as JSON travels JSON-quoted and
 * returns as exactly that text; anything else travels bare.
 */
export function routerSearchValue(value: string): string {
  try {
    JSON.parse(value);
  } catch {
    return value;
  }
  return JSON.stringify(value);
}

/**
 * A route's search params as plain strings, for a page that reads `cf.` keys or
 * report parameters from its URL. The router's default parser runs
 * `JSON.parse` on every value, so a hand-built link's `cf.Year.eq=2026` (or an
 * account code, or `true`) arrives as a number or boolean; a string-only filter
 * would drop it and the page would lose that part of the slice. Primitives are
 * turned back into their text (`JSON.parse` keeps `2026`, `10001`, `true`
 * exactly; `2026.0` reads back as `2026`). Objects, arrays and null are left
 * out of the result, but not out of the page's search: the router builds a
 * match's search as `{...parentSearch, ...strictSearch}`, so a key this omits
 * still reaches `useSearch` with its parsed value. Consumers re-check types.
 */
export function searchAsStrings(search: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(search)) {
    if (typeof v === "string") out[k] = v;
    else if (typeof v === "number" || typeof v === "boolean") out[k] = String(v);
  }
  return out;
}
